import { useMemo } from "react";

// Rough, hand-crafted paths for the two main islands of Aotearoa.
// These are stylised (field-sketch feel), not geographically precise.
// Values are in the lng/lat range then projected into SVG coords below.

// Anchor points defined in [lng, lat]; joined with catmull-rom-ish smoothing.
const NORTH_ISLAND = [
  [172.6, -34.5],
  [173.5, -35.0],
  [174.7, -36.9], // Auckland
  [175.6, -36.5],
  [176.5, -37.5],
  [178.6, -37.7], // East Cape
  [178.1, -38.6],
  [177.9, -39.4],
  [176.9, -39.6], // Napier
  [176.5, -40.5],
  [175.3, -41.6], // Wellington
  [174.6, -41.4],
  [174.0, -40.6],
  [174.4, -40.0],
  [173.8, -39.4], // Taranaki-ish
  [174.1, -38.6],
  [173.5, -37.9],
  [173.0, -36.6],
  [172.9, -35.3],
  [172.6, -34.5],
];

const SOUTH_ISLAND = [
  [172.7, -40.5], // Cape Farewell
  [173.9, -41.0],
  [174.2, -41.8],
  [174.0, -42.6], // Kaikoura area
  [173.3, -43.6], // Banks Peninsula sticking out
  [173.1, -43.9],
  [172.4, -43.9],
  [171.6, -44.4],
  [170.9, -45.4],
  [170.4, -45.9], // Dunedin
  [169.6, -46.5],
  [168.4, -46.6], // Bluff
  [167.4, -46.3],
  [166.6, -45.7],
  [166.5, -44.9], // Fiordland
  [167.4, -44.1],
  [168.4, -43.6],
  [169.7, -42.9], // West coast bulge
  [170.6, -42.4],
  [171.3, -41.7],
  [172.2, -41.1],
  [172.7, -40.5],
];

// Bounding box used to map lng/lat -> SVG px
const BOUNDS = { lngMin: 165.5, lngMax: 179.5, latMin: -47.5, latMax: -33.5 };
export const MAP_VIEW = { w: 520, h: 620 };

export const project = (lng, lat) => {
  const x = ((lng - BOUNDS.lngMin) / (BOUNDS.lngMax - BOUNDS.lngMin)) * MAP_VIEW.w;
  const y = MAP_VIEW.h - ((lat - BOUNDS.latMin) / (BOUNDS.latMax - BOUNDS.latMin)) * MAP_VIEW.h;
  return [x, y];
};

// Inverse projections used by the interactive slice picker
export const xToLng = (x) =>
  BOUNDS.lngMin + (Math.max(0, Math.min(MAP_VIEW.w, x)) / MAP_VIEW.w) * (BOUNDS.lngMax - BOUNDS.lngMin);
export const lngToX = (lng) =>
  ((Math.max(BOUNDS.lngMin, Math.min(BOUNDS.lngMax, lng)) - BOUNDS.lngMin) /
    (BOUNDS.lngMax - BOUNDS.lngMin)) *
  MAP_VIEW.w;
export const yToLat = (y) =>
  BOUNDS.latMax -
  (Math.max(0, Math.min(MAP_VIEW.h, y)) / MAP_VIEW.h) *
    (BOUNDS.latMax - BOUNDS.latMin);
export const latToY = (lat) =>
  MAP_VIEW.h -
  ((Math.max(BOUNDS.latMin, Math.min(BOUNDS.latMax, lat)) - BOUNDS.latMin) /
    (BOUNDS.latMax - BOUNDS.latMin)) *
    MAP_VIEW.h;
export const MAP_BOUNDS = BOUNDS;

// Deterministic small jitter so the outline feels hand-drawn instead of digital.
const jitter = (seed) => {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return (s / 4294967296) * 2 - 1;
  };
};

const buildPath = (points, seed) => {
  const j = jitter(seed);
  const pts = points.map(([lng, lat]) => {
    const [x, y] = project(lng, lat);
    return [x + j() * 3.5, y + j() * 3.5];
  });
  // Simple catmull-rom to bezier
  let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)} `;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i === 0 ? i : i - 1];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2 < pts.length ? i + 2 : i + 1];
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += `C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)} `;
  }
  d += "Z";
  return d;
};

export const useIslandPaths = () =>
  useMemo(
    () => ({
      north: buildPath(NORTH_ISLAND, 7),
      south: buildPath(SOUTH_ISLAND, 42),
      // Slightly offset ghost outlines for a double-inked feel
      northGhost: buildPath(NORTH_ISLAND, 91),
      southGhost: buildPath(SOUTH_ISLAND, 13),
    }),
    []
  );

// A few named locality dots so the map reads even without quakes yet
export const LANDMARKS = [
  { name: "Auckland", lng: 174.76, lat: -36.85 },
  { name: "Wellington", lng: 174.78, lat: -41.29 },
  { name: "Christchurch", lng: 172.64, lat: -43.53 },
  { name: "Dunedin", lng: 170.5, lat: -45.88 },
];
